// =====================================================================
// Which Twilio edge the browser connects to.
//
// Twilio's default is `roaming`: it measures latency at registration and picks an
// edge. Production has shown it picking a materially worse one for some SDRs.
//
// Two layers answer that. A per-session ARM — a link or the picker — measures one
// SDR against themselves, and always wins. Under it sits a geographic DEFAULT for
// populations whose answer is already measured, deliberately NOT stored: an SDR
// must not have to select it, remember it, or survive a cleared browser to keep it.
//
// Pure: no storage, no `window`, no clock, no `Intl`. The hook owns all of that.
// =====================================================================

// Edges this experiment can select. An allowlist, not a passthrough: the value
// originates in a URL and is handed to the Twilio SDK. `sao-paulo` is the CONTROL
// arm, not a wrong answer. `roaming` is absent on purpose — it is the no-override
// default, reached by omitting the option rather than by naming it.
export const SELECTABLE_EDGES = ['frankfurt', 'dublin', 'sao-paulo', 'singapore'] as const;
export type SelectableEdge = (typeof SELECTABLE_EDGES)[number];

// A chain may end in `roaming`, which is not a selectable arm but is a valid entry
// in Twilio's edge array.
export type EdgeChainEntry = SelectableEdge | 'roaming';

// `sao-paulo` and friends are Twilio infrastructure names. Shown raw to someone doing
// timed phone work they read as a bug, and a European SDR picking the one that looks
// like a place would silently put themselves on the control arm. A Record over
// SelectableEdge means adding an edge without a label is a compile error.
const EDGE_LABELS: Record<SelectableEdge, string> = {
  frankfurt: 'Germany (Frankfurt)',
  dublin: 'Ireland (Dublin)',
  'sao-paulo': 'Brazil (São Paulo)',
  singapore: 'Singapore',
};

// Falls back to the raw id: `activeEdge` reports whatever Twilio attached to, which
// can be an edge outside the allowlist entirely.
export function edgeLabel(edge: string | null): string {
  if (edge == null) return 'automatic';
  if (edge === 'roaming') return 'automatic';
  return EDGE_LABELS[edge as SelectableEdge] ?? edge;
}

// Where an edge is known to beat `roaming`, by IANA timezone. `Africa/Johannesburg`
// is the only tz-database zone for South Africa — Cape Town and Durban are not
// zones, and Maseru/Mbabane are Lesotho and Eswatini. Everywhere else is unmeasured,
// which is what the arm is for.
//
// This is a proxy for location and a loose one: it reads the machine's clock, so an
// SA laptop left on UTC gets nothing while a Londoner set to Johannesburg gets
// Frankfurt. Both degrade to an ordinary session, which is why a wrong answer is
// affordable — but it is why the resolved zone is logged.
const GEO_DEFAULT_EDGES: ReadonlyMap<string, SelectableEdge> = new Map([
  ['Africa/Johannesburg', 'frankfurt'],
]);

// Exact match, and no trimming or case-folding unlike `parseEdge`: ECMA-402 requires
// the zone to come back in IANA's own casing, so anything else is a value we should
// not be guessing at.
export function defaultEdgeFor(timeZone: string | null): SelectableEdge | null {
  return timeZone ? (GEO_DEFAULT_EDGES.get(timeZone) ?? null) : null;
}

const EDGE_PARAM = 'edge';
// The one value meaning "reset". Anything else unrecognised is an error, not a
// reset — see `EdgeParam`.
const EDGE_CLEAR = 'default';

export const LS_EDGE = 'maestro.twilio.edge';

// Outlives a shift, self-clears before the next one, so a forgotten arm cannot
// follow an SDR into a second day of data. Without an expiry the only recovery is
// clearing site data, which would also destroy their saved microphone and speaker.
export const ARM_TTL_MS = 16 * 60 * 60 * 1000;

// Rejected values are logged, and a URL can carry arbitrary length. Longer than
// every allowlist entry, so a real typo still round-trips intact.
const MAX_REPORTED_RAW = 32;

// Only set alongside a chain, and this is why the chain needs it. The option
// defaults to 0, which makes `maxPreferredDurationMs` 0, which makes the SDK's
// "reconnect to the edge this call is on" branch expire on its first tick — it then
// rotates to the NEXT entry in the array. Twilio can only re-establish a lost
// websocket on its original edge, so a chain WITHOUT this turns a recoverable
// mid-call signalling blip into a dropped call.
//
// Cost, and why this is 10s rather than Twilio's suggested 30s: Twilio holds the
// call open until it expires, so the `completed` status callback the core's
// occupancy accounting reads is late by the same amount — against a 60s call reaper.
// That was one instrumented SDR's risk when only a link could arm; the geographic
// default makes it a whole team's. 10s still rides out a real blip.
export const SIGNALING_TIMEOUT_MS = 10_000;

// Never a lone edge. A single entry means the SDK retries that one host forever,
// so an unreachable arm would leave an SDR unable to register for a whole shift.
// `roaming` terminates every chain: if the pinned edge is unreachable, fall back to
// exactly what an unarmed SDR does.
export function edgeChain(edge: SelectableEdge): EdgeChainEntry[] {
  return [edge, 'roaming'];
}

// Case- and whitespace-insensitive: typed into a URL bar by a human about to start
// a shift.
export function parseEdge(raw: unknown): SelectableEdge | null {
  if (typeof raw !== 'string') return null;
  const v = raw.trim().toLowerCase();
  return (SELECTABLE_EDGES as readonly string[]).includes(v) ? (v as SelectableEdge) : null;
}

export type EdgeParam =
  | { kind: 'set'; edge: SelectableEdge }
  | { kind: 'clear' }
  | { kind: 'invalid'; raw: string }
  | { kind: 'absent' };

// Four outcomes, not three. `invalid` is deliberately distinct from `absent` so the
// caller can keep the existing arm AND report the typo: a mistyped link that
// silently reset would have an SDR work a full shift producing control data that
// reads as treatment, which corrupts the experiment more quietly than it fails it.
export function edgeParamOf(search: string): EdgeParam {
  const raw = new URLSearchParams(search).get(EDGE_PARAM);
  if (raw == null) return { kind: 'absent' };
  const v = raw.trim().toLowerCase();
  if (v === '' || v === EDGE_CLEAR) return { kind: 'clear' };
  const parsed = parseEdge(v);
  return parsed
    ? { kind: 'set', edge: parsed }
    : { kind: 'invalid', raw: v.slice(0, MAX_REPORTED_RAW) };
}

// How an arm came to exist. Recorded because the picker lets an SDR arm THEMSELVES,
// and a self-selected edge is not a controlled assignment — someone choosing what
// feels fast is exactly the bias the experiment exists to avoid. Keeping the
// provenance means an analysis can filter to operator-assigned arms instead of
// having to trust that nobody touched the control.
export type ArmSource = 'url' | 'picker';

// Wider than storage: `geo` is derived at Device build and never written, and
// keeping it out of `ArmSource` makes that the compiler's problem rather than a
// comment's.
export type EdgeSource = ArmSource | 'geo';

export interface Arm {
  // A DELIBERATE none, not "no arm" — an absent record falls through to the
  // geographic default, so opting out has to be sayable in storage.
  edge: SelectableEdge | null;
  source: ArmSource;
}

// `source` is null whenever `edge` is, so a report cannot claim provenance for a
// connection nobody pinned.
export interface ResolvedEdge {
  edge: SelectableEdge | null;
  source: EdgeSource | null;
}

// A stored record wins, INCLUDING an opt-out: a control arm must not be converted to
// treatment by someone's timezone.
export function resolveEdge(arm: Arm | null, geo: SelectableEdge | null): ResolvedEdge {
  if (arm) return { edge: arm.edge, source: arm.edge ? arm.source : null };
  return geo ? { edge: geo, source: 'geo' } : { edge: null, source: null };
}

// A record rather than a bare string, so the TTL and the provenance are both
// enforceable. Anything malformed reads as "no arm" rather than throwing on a path
// that runs before every call.
export function encodeArm(arm: Arm, nowMs: number): string {
  return JSON.stringify({ edge: arm.edge, source: arm.source, setAt: nowMs });
}

export function decodeArm(raw: string | null, nowMs: number): Arm | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { edge?: unknown; source?: unknown; setAt?: unknown };
    const rawEdge = v?.edge;
    const edge = rawEdge === null ? null : parseEdge(rawEdge);
    // An explicit null is the SDR choosing automatic; a missing or unreadable edge is
    // a record we cannot trust. Collapsing the two would break the opt-out.
    if (edge === null && rawEdge !== null) return null;
    const setAt = typeof v?.setAt === 'number' && Number.isFinite(v.setAt) ? v.setAt : null;
    if (setAt == null) return null;
    const age = nowMs - setAt;
    // A negative age means the clock moved backwards since the arm was written, not
    // that the arm is fresh — without this it would outlive the TTL by the skew.
    if (age < 0) return null;
    // The TTL is there so a forgotten ARM cannot follow an SDR into another shift. An
    // opt-out is not an arm: expiring it would re-pin someone who explicitly said no,
    // overnight and without telling them. The picker offers it as a preference, so it
    // has to last like one.
    if (edge !== null && age > ARM_TTL_MS) return null;
    // Anything written before provenance existed came from a link, since that was
    // the only way to arm. Defaulting the other way would relabel operator-assigned
    // arms as self-selected and quietly shrink the trustworthy population.
    return { edge, source: v?.source === 'picker' ? 'picker' : 'url' };
  } catch {
    return null;
  }
}

// The Device and preflight option fragments live here, beside the allowlist, rather
// than being spread at the call sites: that keeps the shape testable against the
// real expression instead of a copy of it, and keeps every edge decision in one
// file. Both return an EMPTY object when no edge resolved — neither armed nor
// covered by a regional default — so those sessions hand the SDK option objects
// identical to the ones that shipped before any of this existed.
export function deviceEdgeOptions(
  edge: SelectableEdge | null,
): { edge: EdgeChainEntry[]; maxCallSignalingTimeoutMs: number } | Record<string, never> {
  return edge ? { edge: edgeChain(edge), maxCallSignalingTimeoutMs: SIGNALING_TIMEOUT_MS } : {};
}

// Preflight takes a single edge, not a chain, and has no signalling-timeout option.
export function preflightEdgeOptions(
  edge: SelectableEdge | null,
): { edge: SelectableEdge } | Record<string, never> {
  return edge ? { edge } : {};
}
